import { Trans } from "@lingui/macro";
import { IUiDialogResponsiveProps, UiDialogResponsive } from "@smp/ui";
import { saveAs } from "file-saver";
import { useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { EIDBObjectStore, useContextIndexedDb } from "@/context/indexed_db";
import { InvoiceForm } from "@/screens/app/components/invoice_form";
import { useContextPostConstructorOpener } from "@/screens/app/context/post_constructor_opener";
import { gscSendUserParams } from "@/utils";

import { useContextDialogPurchase } from "../context";
import { Prepare } from "./prepare";
import { PurchasePayment } from "./purchase_payment";

export const DialogPurchase = ({
  onClose,
  open,
  onSuccess,
  DialogProps,
}: {
  onClose: () => void;
  open: boolean;
  onSuccess?: () => void;
  DialogProps?: Omit<
    IUiDialogResponsiveProps,
    "isLoading" | "onClose" | "open" | "ref" | "title"
  >;
}) => {
  const navigate = useNavigate();
  const {
    actions: { open: openPc },
  } = useContextPostConstructorOpener();
  const {
    actions: { isInvoiceEditingToggle, resetPurchaseStatus },
    state: {
      isLoading,
      isInvoiceEditing,
      purchaseStatus,
      purchaseBill,
      purchaseRecurringPayment,
    },
  } = useContextDialogPurchase();
  const isGoalReached = useRef(false);
  const { get: getSuccessAction, clear: clearSuccessAction } =
    useContextIndexedDb(EIDBObjectStore.PAYMENT_SUCCESS_ACTION);

  const paymentId = purchaseBill?.id || purchaseRecurringPayment?.id;

  const refDialog = useRef<HTMLDivElement>(null);

  const handleClose = useCallback(
    async (fireSuccessAction?: boolean) => {
      if (!isGoalReached.current) {
        gscSendUserParams({ is_payment_canceled: true });
      }

      isGoalReached.current = false;

      if (isInvoiceEditing) {
        return isInvoiceEditingToggle();
      }

      onClose();

      if (paymentId && (purchaseStatus === "complete" || fireSuccessAction)) {
        onSuccess && onSuccess();

        const successCallback = await getSuccessAction(paymentId);

        await clearSuccessAction();

        const queryParams = new URLSearchParams(window.location.search);

        if (queryParams.get("close_on_success")) {
          window.close();
        } else if (successCallback?.type === "post_constructor_v3") {
          openPc(successCallback.value);
        } else if (successCallback?.type === "redirect") {
          navigate(successCallback.value, {
            state: successCallback.state,
          });
        } else if (successCallback?.type === "download") {
          successCallback.value.forEach(({ dataURL, filename }) => {
            saveAs(dataURL, filename);
          });
        }
      }
    },
    [
      clearSuccessAction,
      getSuccessAction,
      isInvoiceEditing,
      isInvoiceEditingToggle,
      navigate,
      onClose,
      onSuccess,
      openPc,
      paymentId,
      purchaseStatus,
    ],
  );

  const handleReachGoal = () => {
    isGoalReached.current = true;
  };

  return (
    <UiDialogResponsive
      fullWidth
      isLoading={isLoading}
      maxWidth="md"
      onClose={() => handleClose()}
      TransitionProps={{
        onExited: resetPurchaseStatus,
      }}
      open={open}
      ref={refDialog}
      title={!isInvoiceEditing ? <Trans>Payment</Trans> : null}
      {...DialogProps}
    >
      {!purchaseStatus ? (
        isInvoiceEditing ? (
          <InvoiceForm isModal onClose={isInvoiceEditingToggle} />
        ) : (
          <Prepare />
        )
      ) : (
        <PurchasePayment
          onClose={handleClose}
          refDialog={refDialog}
          onReachGoal={handleReachGoal}
        />
      )}
    </UiDialogResponsive>
  );
};
